import { Banknote, ChevronDown, Gavel } from "lucide-react";

import { amountLabel } from "@web/features/terminal/presentation";
import type { IndexedLoan, LoanLiveSnapshot } from "@stonkland/protocol/api-types";

type LoansViewProps = {
  account?: string;
  busy?: string;
  borrowFee?: bigint;
  durationDays: number;
  loanId: string;
  loanPrincipal?: bigint;
  loans: IndexedLoan[];
  liveLoans: Record<string, LoanLiveSnapshot>;
  loanApyBps?: number;
  gracePeriod?: bigint;
  liquidationIncentiveBps?: number;
  minDuration?: bigint;
  maxDuration?: bigint;
  ownedTokens: bigint[];
  protocolLive: boolean;
  selectedToken?: bigint;
  onBorrow: () => Promise<void>;
  onDuration: (days: number) => void;
  onLiquidate: () => Promise<void>;
  onLoanId: (id: string) => void;
  onRepay: () => Promise<void>;
  onSelectToken: (tokenId: bigint) => void;
};

export function LoansView(props: LoansViewProps) {
  const minDays = props.minDuration ? Number(props.minDuration / 86400n) : 365;
  const maxDays = props.maxDuration ? Number(props.maxDuration / 86400n) : 730;
  const graceDays = props.gracePeriod ? Number(props.gracePeriod / 86400n) : 3;
  const selectedLoan = props.loanId ? props.liveLoans[props.loanId] : undefined;
  const selectedIsBorrower = Boolean(
    selectedLoan &&
      props.account &&
      selectedLoan.borrower.toLowerCase() === props.account.toLowerCase(),
  );
  const dateLabel = (timestamp?: number) =>
    timestamp
      ? new Intl.DateTimeFormat("en", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(timestamp * 1_000))
      : "—";
  return (
    <div className="terminal-grid">
      <article className="panel portfolio-panel">
        <div className="panel-head">
          <div>
            <span>BORROW AGAINST A BROKER</span>
            <strong>LOCK ONE. BORROW TOKENS.</strong>
          </div>
          <Banknote size={18} />
        </div>
        <div className="asset-table">
          <div className="asset-row table-labels">
            <span>PRINCIPAL</span>
            <span>APR</span>
            <span>TERM</span>
            <span>COLLATERAL</span>
          </div>
          <div className="asset-row">
            <span data-label="Principal">
              <b>{amountLabel(props.loanPrincipal, 0)} LAND</b>
            </span>
            <span data-label="APR">
              {props.loanApyBps !== undefined
                ? `${props.loanApyBps / 100}%`
                : "—"}
            </span>
            <span data-label="Term">
              {minDays}–{maxDays} DAYS
            </span>
            <span data-label="Collateral">1 BROKER NFT</span>
          </div>
        </div>
        {props.loans.length > 0 && (
          <div className="loan-list">
            {props.loans.slice(0, 8).map((loan) => (
              <button
                className={`loan-row ${props.loanId === loan.loanId ? "selected" : ""}`}
                key={loan.loanId}
                onClick={() => props.onLoanId(loan.loanId)}
              >
                {(() => {
                  const live = props.liveLoans[loan.loanId];
                  return (
                    <>
                      <span>
                        <small>LOAN</small>
                        <b>#{loan.loanId}</b>
                      </span>
                      <span>
                        <small>BROKER</small>
                        <b>#{loan.tokenId}</b>
                      </span>
                      <span>
                        <small>MATURITY</small>
                        <b>{dateLabel(live?.maturityTimestamp)}</b>
                      </span>
                      <span>
                        <small>GRACE DEADLINE</small>
                        <b>{dateLabel(live?.graceDeadline)}</b>
                      </span>
                      <span>
                        <small>LATE FEE NOW</small>
                        <b>
                          {live
                            ? `${amountLabel(BigInt(live.lateFee), 5)} ETH`
                            : "—"}
                        </b>
                      </span>
                      <span>
                        <small>STATE</small>
                        <b
                          className={
                            live?.liquidationEligible
                              ? "negative"
                              : loan.status === "active"
                                ? "positive"
                                : ""
                          }
                        >
                          {live?.liquidationEligible
                            ? "LIQUIDATABLE"
                            : loan.status.toUpperCase()}
                        </b>
                      </span>
                      <em>{`${loan.borrower.slice(0, 6)}…${loan.borrower.slice(-4)}`}</em>
                    </>
                  );
                })()}
              </button>
            ))}
          </div>
        )}
        <p className="panel-note">
          Lock one Broker and receive the fixed loan amount. Repay before the
          deadline to get it back. If the grace period expires, the Broker can
          be liquidated and returned to the trading inventory.
        </p>
      </article>
      <aside className="panel action-panel">
        <div className="panel-head">
          <div>
            <span>LOAN ACTIONS</span>
            <strong>BORROW / REPAY</strong>
          </div>
          <Gavel size={18} />
        </div>
        <label className="field">
          <span>COLLATERAL BROKER</span>
          <select
            value={props.selectedToken?.toString() || ""}
            onChange={(event) =>
              props.onSelectToken(BigInt(event.target.value))
            }
          >
            {props.ownedTokens.map((id) => (
              <option key={id.toString()} value={id.toString()}>
                Broker #{id.toString().padStart(4, "0")}
              </option>
            ))}
          </select>
          <ChevronDown size={15} />
        </label>
        <label className="field">
          <span>DURATION (DAYS)</span>
          <input
            type="number"
            min={minDays}
            max={maxDays}
            value={props.durationDays}
            onChange={(event) => props.onDuration(Number(event.target.value))}
          />
        </label>
        <div className="transaction-breakdown compact-breakdown">
          <span>
            <small>Principal received</small>
            <b>{amountLabel(props.loanPrincipal, 0)} LAND</b>
          </span>
          <span>
            <small>Upfront ETH fee</small>
            <b>
              {props.borrowFee !== undefined
                ? `${amountLabel(props.borrowFee, 5)} ETH`
                : "—"}
            </b>
          </span>
          <span>
            <small>Fee routing</small>
            <b>70% rewards · 30% treasury</b>
          </span>
          <span>
            <small>Grace / liquidation</small>
            <b>
              {graceDays} days · {(props.liquidationIncentiveBps ?? 0) / 100}%
              incentive
            </b>
          </span>
        </div>
        <button
          className="ticket-button"
          onClick={() => void props.onBorrow()}
          disabled={!props.protocolLive || !props.selectedToken}
        >
          {props.busy === "borrow"
            ? "BORROWING…"
            : `BORROW ${amountLabel(props.loanPrincipal, 0)} LAND`}
        </button>
        <label className="field">
          <span>LOAN ID</span>
          <input
            inputMode="numeric"
            value={props.loanId}
            onChange={(event) => props.onLoanId(event.target.value)}
            placeholder="1"
          />
        </label>
        {selectedLoan && (
          <div className="loan-ticket-detail">
            <span>
              STARTED <b>{dateLabel(selectedLoan.startTimestamp)}</b>
            </span>
            <span>
              MATURITY <b>{dateLabel(selectedLoan.maturityTimestamp)}</b>
            </span>
            <span>
              GRACE DEADLINE <b>{dateLabel(selectedLoan.graceDeadline)}</b>
            </span>
            <span>
              LATE FEE <b>{amountLabel(BigInt(selectedLoan.lateFee), 5)} ETH</b>
            </span>
          </div>
        )}
        <button
          className="secondary-ticket"
          onClick={() => void props.onRepay()}
          disabled={
            !props.protocolLive ||
            !props.loanId ||
            !selectedIsBorrower ||
            selectedLoan?.status !== 1
          }
        >
          {props.busy === "repay" ? "REPAYING…" : "REPAY + RETURN NFT"}
        </button>
        <button
          className="clock-out-button"
          onClick={() => void props.onLiquidate()}
          disabled={
            !props.protocolLive ||
            !props.loanId ||
            !selectedLoan?.liquidationEligible
          }
        >
          {props.busy === "liquidate"
            ? "LIQUIDATING…"
            : selectedLoan?.liquidationEligible
              ? "LIQUIDATE NOW"
              : "NOT YET LIQUIDATABLE"}
        </button>
      </aside>
    </div>
  );
}
